import React, {useCallback, useEffect, useRef, useState} from 'react';
import ReactDOMServer from "react-dom/server";
import {loadList} from '../../redux/modules/aws-bucket';
import Viewer from 'react-viewer';
import 'react-viewer/dist/index.css';
import '../../style/css/xray-viewer.css';
import {saveUdaUsage} from "../../redux/modules/billing";
import {getPaddedPatNum} from "../../utils/commonutils";

const METADATA_ID = 'xray_metadata_viewer';
const HIDDEN_METADATA_KEYS = ['full_name', 'type'];

export default function XrayViewer({dispatch, practice, patientNumber}) {
    const [visible, setVisible] = useState(false);
    const [selected, setSelected] = useState(null);
    const [images, setImages] = useState(null);

    // Keep the latest selected available to the stable DOM-injection callback.
    const selectedRef = useRef(selected);
    selectedRef.current = selected;

    useEffect(() => {
        dispatch(loadList('ndp-images-and-xrays', `${practice}/${getPaddedPatNum(patientNumber)}/xrays`))
            .then(result => setImages(result.reverse()));
    }, [dispatch, practice, patientNumber]);

    const renderMetaData = useCallback(() => {
        const current = selectedRef.current;
        if (!current || !current.metadata) return null;
        const metadata = current.metadata;
        const list = Object.keys(metadata)
            .filter(key => !HIDDEN_METADATA_KEYS.includes(key))
            .sort()
            .map(key => {
                const name = key.replace('_', ' ').toLocaleUpperCase();
                return (
                    <span className="col p-1" key={key}>
                        <small>{name}</small>:<br/> <b style={{marginLeft: '.2em'}}>{metadata[key]}</b>
                    </span>
                );
            });
        return <p id={METADATA_ID} className="row xray-metadata-viewer">{list}</p>;
    }, []);

    const updateMetadata = useCallback(() => {
        setTimeout(() => {
            const element = document.getElementById(METADATA_ID);
            if (element) element.parentNode.removeChild(element);
            const metadata = renderMetaData();
            if (!metadata) return;
            const html = ReactDOMServer.renderToStaticMarkup(metadata);
            document.body.insertAdjacentHTML('beforeend', html);
        }, 200);
    }, [renderMetaData]);

    const onViewXrays = useCallback(() => {
        if (!images || images.length === 0) return;
        setSelected(images[0]);
        selectedRef.current = images[0];
        setVisible(true);
        updateMetadata();
        dispatch(saveUdaUsage('xrayViewer', ' '));
    }, [images, updateMetadata, dispatch]);

    const closeViewer = useCallback(() => {
        setVisible(false);
        setSelected(null);
        selectedRef.current = null;
        updateMetadata();
        window.history.replaceState(null, null, ' ');
    }, [updateMetadata]);

    const handleBackPress = useCallback(() => {
        if (window.location.hash === '#xray-open') {
            setVisible(true);
            return;
        }
        closeViewer();
    }, [closeViewer]);

    const imageViewerChange = useCallback((next) => {
        setSelected(next);
        selectedRef.current = next;
        updateMetadata();
    }, [updateMetadata]);

    useEffect(() => {
        window.onhashchange = handleBackPress;
        return () => {
            if (window.onhashchange === handleBackPress) {
                window.onhashchange = null;
            }
        };
    }, [handleBackPress]);

    const renderText = () => {
        if (!images) return <p className="mt-2 text-muted"><small>...loading xrays</small></p>;
        if (images.length < 1) return <p className="mt-2 text-muted">No Xrays Found</p>;
        return (
            <p className="primaryColor cursorPointer mt-2" onClick={onViewXrays}>
                <a href="#xray-open">View Xrays</a>
            </p>
        );
    };

    return (
        <span>
            {renderText()}
            <Viewer
                images={images}
                loadOnInit={true}
                defaultScale={1.8}
                onChange={imageViewerChange}
                onClose={closeViewer}
                visible={visible}>
            </Viewer>
        </span>
    );
}
